import { useEffect } from "react";
import { useLang } from "../lib/LangContext";

/**
 * SEO manager — updates <title>, meta description, canonical URL, OG tags,
 * hreflang alternates, and injects a JSON-LD block.
 */
export default function SEO({ page = "home", canonicalPath = "/", extraJsonLd = null }) {
  const { lang } = useLang();

  useEffect(() => {
    // Production canonical base — matches homepage in package.json.
    // All canonical & hreflang links MUST point to the deployed sub-path,
    // regardless of the environment the app is served from (preview / prod).
    const PROD_BASE = "https://ariovis.fr/produits/roadmap-generator";
    // Absolute URL to the self-hosted brand logo (og/twitter cards must be absolute).
    const OG_LOGO = `${PROD_BASE}/logos/ariovis-logo-light.svg`;
    // Normalize canonicalPath so PROD_BASE + path yields a clean URL.
    const path = canonicalPath === "/" ? "/" : canonicalPath.replace(/^\/+/, "/");
    const url = `${PROD_BASE}${path === "/" ? "/" : path}`;

    const meta = {
      home: {
        fr: {
          title: "Ma Roadmap IAM Zero Trust — Ariovis",
          description: "Évaluez la maturité de votre IAM et obtenez une roadmap personnalisée en cinq étapes vers le Zero Trust. Étude gratuite Ariovis.",
        },
        en: {
          title: "My IAM Zero Trust Roadmap — Ariovis",
          description: "Assess your IAM maturity and get a personalized 5-step roadmap towards Zero Trust. Free Ariovis study.",
        },
      },
      wizard: {
        fr: { title: "Diagnostic IAM Zero Trust — Ariovis", description: "Répondez au questionnaire Ariovis pour obtenir votre roadmap IAM Zero Trust personnalisée." },
        en: { title: "IAM Zero Trust Assessment — Ariovis", description: "Answer the Ariovis questionnaire to get your personalized IAM Zero Trust roadmap." },
      },
      results: {
        fr: { title: "Votre roadmap IAM Zero Trust — Ariovis", description: "Consultez votre niveau de maturité IAM, vos écarts prioritaires et votre roadmap Zero Trust." },
        en: { title: "Your IAM Zero Trust roadmap — Ariovis", description: "View your IAM maturity level, priority gaps and Zero Trust roadmap." },
      },
    };
    const cur = (meta[page] && meta[page][lang]) || meta.home[lang] || meta.home.fr;

    // Title & desc
    document.title = cur.title;
    setMeta("description", cur.description);

    // Language
    document.documentElement.setAttribute("lang", lang);

    // Canonical
    setLinkRel("canonical", url);

    // hreflang alternates
    setAlternate("fr", `${PROD_BASE}${path === "/" ? "/" : path}?lang=fr`);
    setAlternate("en", `${PROD_BASE}${path === "/" ? "/" : path}?lang=en`);
    setAlternate("x-default", `${PROD_BASE}${path === "/" ? "/" : path}`);

    // Open Graph
    setMetaProp("og:title", cur.title);
    setMetaProp("og:description", cur.description);
    setMetaProp("og:type", "website");
    setMetaProp("og:url", url);
    setMetaProp("og:site_name", "Ariovis");
    setMetaProp("og:locale", lang === "fr" ? "fr_FR" : "en_US");
    setMetaProp("og:image", OG_LOGO);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", cur.title);
    setMeta("twitter:description", cur.description);
    setMeta("twitter:image", OG_LOGO);

    // Robots
    setMeta("robots", "index,follow,max-image-preview:large");

    // JSON-LD
    const jsonld = extraJsonLd || {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: cur.title,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      publisher: {
        "@type": "Organization",
        name: "Ariovis",
        url: "https://www.ariovis.fr",
        logo: OG_LOGO,
      },
      inLanguage: lang,
      url,
      description: cur.description,
    };
    setJsonLd(jsonld);
  }, [lang, page, canonicalPath, extraJsonLd]);

  return null;
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setMetaProp(prop, content) {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLinkRel(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]:not([hreflang])`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function setAlternate(hreflang, href) {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function setJsonLd(data) {
  let el = document.getElementById("ariovis-jsonld");
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", "ariovis-jsonld");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
